import React from 'react';

/**
 * Minimalny zestaw ikon nawigacji (obrys 2px, currentColor — dziedziczą kolor tekstu).
 * Jedno źródło dla sidebara (desktop) i bottom bara (mobile); dobór po ścieżce trasy
 * przez iconForPath(), żeby nie dublować mapowania w obu miejscach.
 */
export type IconName =
  | 'machines' | 'tickets' | 'notes' | 'clipboard' | 'docs' | 'menu' | 'close'
  | 'customers' | 'factory' | 'building' | 'receipt' | 'chart' | 'coins' | 'database'
  | 'settings' | 'shield' | 'mail' | 'credit-card' | 'briefcase' | 'user'
  | 'key' | 'logout' | 'tag' | 'image' | 'wrench';

const PATHS: Record<IconName, React.ReactNode> = {
  machines: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <rect x="9" y="9" width="6" height="6" />
      <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" />
    </>
  ),
  tickets: (
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  ),
  notes: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
    </>
  ),
  clipboard: (
    <>
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="M12 11h4M12 16h4M8 11h.01M8 16h.01" />
    </>
  ),
  docs: (
    <>
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </>
  ),
  menu: <path d="M4 6h16M4 12h16M4 18h16" />,
  close: <path d="M18 6 6 18M6 6l12 12" />,
  customers: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  factory: (
    <>
      <path d="M2 21h20" />
      <path d="M4 21V9l6 4V9l6 4V9l4 3v9" />
      <path d="M17 9V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v8" />
      <path d="M7 17h.01M11 17h.01M15 17h.01" />
    </>
  ),
  building: (
    <>
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
      <path d="M10 6h4M10 10h4M10 14h4M10 18h4" />
    </>
  ),
  receipt: (
    <>
      <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
      <path d="M14 8H8M16 12H8M13 16H8" />
    </>
  ),
  chart: (
    <>
      <path d="M3 3v18h18" />
      <path d="M18 17V9M13 17V5M8 17v-3" />
    </>
  ),
  coins: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M18.09 10.37A6 6 0 1 1 10.34 18" />
      <path d="M7 6h1v4M16.71 13.88l.7.71-2.82 2.82" />
    </>
  ),
  database: (
    <>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v14a9 3 0 0 0 18 0V5" />
      <path d="M3 12a9 3 0 0 0 18 0" />
    </>
  ),
  settings: (
    <>
      <path d="M20 7h-9M14 17H5" />
      <circle cx="17" cy="17" r="3" />
      <circle cx="7" cy="7" r="3" />
    </>
  ),
  shield: (
    <path d="M20 13c0 5-3.5 7.5-7.66 8.94a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
  ),
  mail: (
    <>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </>
  ),
  'credit-card': (
    <>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
    </>
  ),
  briefcase: (
    <>
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </>
  ),
  user: (
    <>
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>
  ),
  key: (
    <path d="m21 2-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777Zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4" />
  ),
  logout: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5M21 12H9" />
    </>
  ),
  tag: (
    <>
      <path d="M12 2H2v10l9.29 9.29a1 1 0 0 0 1.42 0l8.58-8.58a1 1 0 0 0 0-1.42Z" />
      <circle cx="7" cy="7" r="1.5" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
    </>
  ),
  wrench: (
    <path d="M14.7 6.3a4 4 0 0 0-5.4 5.28L3 17.88 6.12 21l6.3-6.3a4 4 0 0 0 5.28-5.4l-2.5 2.5-2.4-.6-.6-2.4Z" />
  ),
};

export const NavIcon: React.FC<{ name: IconName; size?: number; className?: string }> = ({
  name, size = 16, className,
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {PATHS[name]}
  </svg>
);

/** Ikona dla trasy nawigacji — wspólne mapowanie sidebara i bottom bara. */
export const iconForPath = (path: string): IconName => {
  if (path.startsWith('/machines')) return 'machines';
  if (path.startsWith('/machine-types')) return 'tag';
  if (path.startsWith('/my-tickets')) return 'tickets';
  if (path.startsWith('/notes')) return 'notes';
  if (path.startsWith('/documentation')) return 'docs';
  // Klienci to firmy-fabryki (właściciele maszyn), nie osoby — stąd fabryka zamiast ludzi.
  if (path.startsWith('/customers')) return 'factory';
  if (path.startsWith('/admin/assignments')) return 'clipboard';
  if (path.startsWith('/admin/integrators')) return 'building';
  if (path.startsWith('/admin/billing')) return 'receipt';
  if (path.startsWith('/admin/stats')) return 'chart';
  if (path.startsWith('/admin/ai-costs')) return 'coins';
  if (path.startsWith('/admin/indexing')) return 'database';
  if (path.startsWith('/admin/users')) return 'customers';
  if (path.startsWith('/admin/settings')) return 'settings';
  if (path.startsWith('/admin/gdpr')) return 'shield';
  if (path.startsWith('/team/members')) return 'customers';
  if (path.startsWith('/team/invitations')) return 'mail';
  if (path.startsWith('/team/subscription')) return 'credit-card';
  if (path.startsWith('/company-account')) return 'briefcase';
  if (path.startsWith('/profile')) return 'user';
  if (path.startsWith('/change-password')) return 'key';
  return 'menu';
};
